import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const HelpCenter = () => {
  const helpTopics = [
    {
      title: "Delivery related",
      faqs: [
        { question: "What should I do if my order is approved but hasn't been shipped yet?", answer: "Check the estimated shipping date in your order details." },
        { question: "Can I take the shipment after opening and checking the contents inside?", answer: "No, shipments must be accepted before opening." },
        { question: "How quickly can I get my order delivered?", answer: "Depends on your location and chosen shipping method." },
        { question: "Order reached hub but not out for delivery?", answer: "High demand or delays. Track your order for updates." },
        { question: "Why can't I track my order?", answer: "Tracking may take time to update. Check back later." },

        { question: "Complaint about delivery executive?", answer: "Report through our customer support section." }
      ]
    },
    {
      title: "Login and my account",
      faqs: [
        { question: "How do I reset my password?", answer: "Click 'Forgot Password' and follow email instructions." },
        { question: "Change registered mobile number?", answer: "Go to account settings to update info." },
        { question: "Deactivate my account?", answer: "Contact customer support for deactivation." },
        { question: "View order history?", answer: "Go to 'My Orders' in your profile." },
        { question: "Login issue?", answer: "Double-check your credentials or reset password." },
        { question: "Same email for multiple accounts?", answer: "No, one email = one account." }
      ]
    },
    
    
  {
    title: "Refunds related",
    faqs: [
      { question: "How long does it take to get a refund?", answer: "Refunds typically take 5-7 business days after the return is processed." },
      { question: "Where will my refund be credited?", answer: "It will be credited to your original payment method or wallet." },
      { question: "I haven't received my refund yet. What should I do?", answer: "Check your bank statement and contact support if it hasn’t arrived in 7 days." },
      { question: "Are shipping charges refunded?", answer: "Shipping charges are only refunded if the return is due to a seller error." },
      { question: "Can I get a refund on gift cards?", answer: "No, gift card purchases are non-refundable." },
      { question: "What happens if the refund fails?", answer: "We will contact you to collect alternate bank details to process the refund." }
    ]
  },
  {
    title: "Fashion EMI",
    faqs: [
      { question: "What is No Cost EMI?", answer: "It's an option where you don't pay any interest on the loan. You only pay the product price in installments." },
      { question: "Which cards are eligible for EMI?", answer: "Most leading credit cards and some debit cards are eligible. Please check during checkout." },
      { question: "Can I foreclose my EMI?", answer: "Yes, but foreclosure terms depend on the bank. Contact your bank for details." },
      { question: "Is EMI available for all products?", answer: "No, EMI is available on selected products. Check the product page for eligibility." },
      { question: "What if my EMI gets rejected by the bank?", answer: "Your order will be canceled and the amount will be refunded if already paid." },
      { question: "Do I need to submit documents for EMI?", answer: "No, documentation is not required if your card is eligible at checkout." }
    ]
  }
];

  const [selectedTopic, setSelectedTopic] = useState(helpTopics[0]);
  const [openIndex, setOpenIndex] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const openTopic = (topic) => {
    setSelectedTopic(topic);
    setOpenIndex(null);
    setShowMore(false);
    setSearchTerm("");
    setIsSidebarOpen(false);
  };

  const filteredFaqs = selectedTopic.faqs
    .filter((faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .slice(0, showMore ? selectedTopic.faqs.length : 3);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col md:flex-row">
      {/* Sidebar */}
      <div className={`bg-gray-100 w-full md:w-1/4 p-6 shadow-md ${isSidebarOpen ? "block" : "hidden"} md:block`}>
        <h2 className="text-lg font-semibold mb-4">HELP TOPICS</h2>
        <ul className="space-y-2 text-gray-700">
          {helpTopics.map((topic, index) => (
            <li
              key={index}
              className={`cursor-pointer hover:text-gray-600 ${
                selectedTopic.title === topic.title ? "text-gray-600 font-semibold" : ""
              }`}
              onClick={() => openTopic(topic)}
            >
              {topic.title}
            </li>
          ))}
        </ul>
      </div>

      {/* Content */}
      <div className="w-full md:w-3/4 p-6">
        {/* Mobile Toggle Button */}
        <button
          className="md:hidden mb-4 text-gray-600 underline"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          {isSidebarOpen ? "Close Topics" : "Open Topics"}
        </button>

        {/* Search */}
        <input
          type="text"
          placeholder="Search FAQs..."
          className="w-full p-2 mb-4 border rounded-md shadow-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {/* Title */}
        <h2 className="text-2xl font-semibold mb-4">{selectedTopic.title}</h2>

        {/* FAQ List */}
        <ul className="space-y-3 text-gray-700">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, index) => (
              <li
                key={index}
                className="border-b pb-2 cursor-pointer"
                onClick={() => toggleFAQ(index)}
              >
                <p className="font-medium">{faq.question}</p>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden text-gray-600 mt-2"
                    >
                      {faq.answer}
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            ))
          ) : (
            <p className="text-gray-500">No FAQs match your search.</p>
          )}
        </ul>

        {/* View More / Less */}
        {selectedTopic.faqs.length > 3 && !searchTerm && (
          <button
            className="mt-4 text-gray-600 hover:underline"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? "Show Less" : "View More"}
          </button>
        )}

       
      </div>
    </div>
  );
};

export default HelpCenter;
